/**
 * T-Stacker 2.0 - Main Entry Point
 * 
 * Custom engine using Rapier 2D physics and HTML5 Canvas rendering.
 */

import { Game, GameStateInfo } from './game/Game';
import { GamePhase, Difficulty } from './game/StateManager';
import { TJunctionResult } from './game/Scoring';
import { DIFFICULTIES } from './game/Constants';
import { HUD } from './ui/HUD';
import { Menu } from './ui/Menu';
import { GameOver } from './ui/GameOver';
import { Celebration } from './game/Celebration';
import { soundManager } from './audio/SoundManager';

/**
 * Get T-Junction bonus for a difficulty level
 */
function getDifficultyBonus(difficulty: Difficulty): number {
    return DIFFICULTIES[difficulty].tJunctionBonus;
}

// UI Components
let hud: HUD;
let menu: Menu;
let gameOver: GameOver;
let celebration: Celebration;
let game: Game;

async function main(): Promise<void> {
    console.log('Initializing T-Stacker 2.0...');

    // Get canvas
    const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
    if (!canvas) {
        throw new Error('Canvas not found');
    }

    // Initialize audio
    soundManager.init();

    // Create game instance
    game = new Game({
        canvas,
        debug: false, // Set to true for debug overlay
    });

    // Create UI components
    hud = new HUD(document.body, { showFps: true, debug: false });
    menu = new Menu(document.body);
    gameOver = new GameOver(document.body, {
        countUpDuration: 2500,
        onRetry: () => game.retry(),
        onMenu: () => game.returnToMenu(),
        onHover: () => soundManager.playHover(),
        onClick: () => soundManager.playClick(),
        onScoreTick: () => soundManager.playScoreUp(),
    });
    celebration = new Celebration(document.body);

    // Wire up menu audio
    menu.setAudioCallbacks(
        () => soundManager.playHover(),
        () => soundManager.playClick()
    );

    // Wire up menu selection
    menu.setOnSelect((difficulty) => {
        soundManager.playGameStart();
        celebration.resetCombo();
        game.startGame(difficulty);
    });

    // Set up game callbacks
    game.setCallbacks({
        onPhaseChange: (newPhase: GamePhase, oldPhase: GamePhase) => {
            console.log(`Phase changed: ${oldPhase} -> ${newPhase}`);
            handlePhaseChange(newPhase, oldPhase);
        },
        onFinishingStart: () => {
            console.log('FINISHING PHASE - Spawning stopped, countdown started!');
            // Note: settling sound now only plays when entering settling phase
        },
        onTimerTick: (seconds: number) => {
            if (seconds <= 5) {
                soundManager.playWarning();
            }
        },
        onTJunction: (result: TJunctionResult) => {
            console.log(`T-Junction detected! Seam at (${result.seamX.toFixed(0)}, ${result.celebrationY.toFixed(0)})`);
            soundManager.playTJunction();
            soundManager.playUwu();
            
            // Get actual T-junction bonus from current difficulty
            const difficulty = game.getStateManager().getDifficulty();
            const bonusPerTJunction = getDifficultyBonus(difficulty);
            celebration.tJunction(result.seamX, result.celebrationY, bonusPerTJunction);
        },
        onStateUpdate: (state: GameStateInfo) => {
            hud.update(state);
        },
        onGrab: () => {
            soundManager.playGrab();
        },
        onRelease: () => {
            soundManager.playRelease();
        },
        onThrow: () => {
            soundManager.playThrow();
        },
    });

    // Initialize
    await game.init();
    console.log('Game initialized');

    // Handle resize
    window.addEventListener('resize', () => {
        console.log('Window resized - refresh to apply new layout');
    });

    // Resume audio on first interaction
    setupAudioResume();

    // Start the game loop (starts in 'menu' phase)
    game.start();
    console.log('Game loop started');

    // Show menu
    hud.hide();
    gameOver.hide();
    menu.show();
}

/**
 * Handle phase transitions
 */
function handlePhaseChange(newPhase: GamePhase, _oldPhase: GamePhase): void {
    switch (newPhase) {
        case 'menu':
            hud.hide();
            gameOver.hide();
            menu.show();
            celebration.resetCombo();
            break;
        case 'playing':
            menu.hide();
            gameOver.hide();
            hud.show();
            break;
        case 'settling':
            // Keep HUD visible during settling
            soundManager.playSettling();
            break;
        case 'gameover':
            soundManager.playGameOver();
            soundManager.playUwuFinale();
            // Short delay before showing game over screen
            setTimeout(() => {
                const finalState = game.getStateInfo();
                
                // Confetti burst for high scores
                if (finalState.score > 5000) {
                    const centerX = window.innerWidth / 2;
                    const centerY = window.innerHeight / 2;
                    celebration.spawnConfetti(centerX, centerY - 100, 30);
                    celebration.spawnStars(centerX, centerY, 15);
                }
                
                gameOver.show(finalState);
            }, 1000);
            break;
    }
}

/**
 * Setup audio context resume on first user interaction
 */
function setupAudioResume(): void {
    const resumeAudio = (): void => {
        soundManager.resume();
        document.removeEventListener('click', resumeAudio);
        document.removeEventListener('touchstart', resumeAudio);
        document.removeEventListener('keydown', resumeAudio);
    };

    document.addEventListener('click', resumeAudio);
    document.addEventListener('touchstart', resumeAudio);
    document.addEventListener('keydown', resumeAudio);
}

// Run the test
main().catch(console.error);
